// Aperçu en deux onglets (05/10, accord de Louka) : « Original » montre le fichier tel quel,
// « Texte extrait » le texte que la plateforme a extrait et indexé. Ce module choisit, sans appel
// réseau, d'où vient ce texte :
//  1. la version « processed » du fichier quand le traitement à l'envoi en produit une
//     (miroir client de PROCESSING_BY_CONTENT_TYPE, front/lib/api/files/processing/index.ts,
//     restreint aux traitements qui rendent du TEXTE : Tika, transcription audio) ;
//  2. sinon le texte du document dans son dossier (route documents de la vue de source) ;
//  3. sinon rien : l'onglet est masqué. Un PDF ou un Word déposé dans une conversation n'a pas de
//     version traitée (l'amont réserve Tika aux autres usages) : pas d'extraction à la demande ici.
import type { DataSourceViewType } from "@app/types/data_source_view";

/** Document d'un dossier : de quoi lire son texte indexé. */
export interface DocumentDeDossier {
  dataSourceView: DataSourceViewType;
  documentId: string;
}

export type SourceTexteExtrait =
  | { type: "fichier"; fileId: string }
  | ({ type: "document" } & DocumentDeDossier);

// Extraction Tika, sauf pour un fichier déposé dans une conversation.
const DOCUMENTS_TIKA = new Set<string>([
  "application/pdf",
  "application/msword",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

// Traités pour tous les usages, conversation comprise (tableurs : Tika ; audio : transcription).
const TOUJOURS_TRAITES = new Set<string>([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "audio/mpeg",
  "audio/wav",
  "audio/x-wav",
  "audio/webm",
  "video/webm",
  "audio/ogg",
  "audio/x-m4a",
]);

function typeNu(contentType: string): string {
  return contentType.split(";")[0].trim().toLowerCase();
}

/** Le fichier a-t-il une version traitée faite de texte ? */
export function aUnTexteExtrait(contentType: string, useCase: string): boolean {
  const type = typeNu(contentType);
  if (TOUJOURS_TRAITES.has(type)) {
    return true;
  }
  return DOCUMENTS_TIKA.has(type) && useCase !== "conversation";
}

export function sourceDuTexteExtrait({
  fichier,
  document,
}: {
  fichier: { fileId: string; contentType: string; useCase: string } | null;
  document: DocumentDeDossier | null;
}): SourceTexteExtrait | null {
  if (
    fichier &&
    fichier.fileId !== "" &&
    aUnTexteExtrait(fichier.contentType, fichier.useCase)
  ) {
    return { type: "fichier", fileId: fichier.fileId };
  }
  if (document && document.documentId !== "") {
    return {
      type: "document",
      dataSourceView: document.dataSourceView,
      documentId: document.documentId,
    };
  }
  return null;
}

/** Le texte d'un document markdown se rend en markdown ; tout le reste en texte brut. */
export function estTexteMarkdown(mimeType: string | null | undefined): boolean {
  if (!mimeType) {
    return false;
  }
  const type = typeNu(mimeType);
  return type === "text/markdown" || type === "text/x-markdown";
}
